// src/pages/PublicQuote.js
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatDate(ts) {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function safeItems(arr) {
  return Array.isArray(arr) ? arr : [];
}

function regionLabel(country) {
  const c = (country || "").toLowerCase().trim();
  if (c === "canada" || c.includes("canada")) return "Province";
  if (c === "ireland" || c.includes("ireland")) return "County";
  if (
    c === "united states" ||
    c === "united states of america" ||
    c === "usa" ||
    c === "us" ||
    c.includes("united states")
  )
    return "State";
  return "Region";
}

export default function PublicQuote() {
  const { quoteId } = useParams();

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      setErr("");
      setLoading(true);

      if (!quoteId) {
        setErr("Missing quote id.");
        setLoading(false);
        return;
      }

      try {
        const ref = doc(db, "quotes", quoteId);
        const snap = await getDoc(ref);

        if (!alive) return;

        if (!snap.exists()) {
          setQuote(null);
          setErr("Quote not found.");
          setLoading(false);
          return;
        }

        const data = { id: snap.id, ...snap.data() };

        if (data.publicEnabled === false) {
          setQuote({ disabled: true });
          setLoading(false);
          return;
        }

        setQuote(data);
        setLoading(false);

        // mark viewed (best-effort)
        try {
          await updateDoc(ref, {
            lastViewedAt: serverTimestamp(),
            status: data.status === "sent" ? "viewed" : data.status || "draft",
          });
        } catch {}
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || String(e));
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [quoteId]);

  const items = useMemo(() => safeItems(quote?.items), [quote?.items]);
  const totals = quote?.totals || {};

  const addressBlock = useMemo(() => {
    const a = quote?.clientAddress;
    if (!a) return "";
    const lines = [];

    if (a.line1) lines.push(a.line1);
    if (a.line2) lines.push(a.line2);

    const cityRegion = [a.city, a.region].filter(Boolean).join(", ");
    const lastLine = [cityRegion, a.postalCode].filter(Boolean).join(" ");
    if (lastLine) lines.push(lastLine);

    if (a.country) lines.push(a.country);

    return lines.join("\n");
  }, [quote?.clientAddress]);

  const createdLabel = quote?.createdAt ? formatDate(quote.createdAt) : "";
  const regionName = quote?.clientAddress?.country ? regionLabel(quote.clientAddress.country) : "Region";

  if (loading) {
    return (
      <section className="section stone">
        <div className="container">
          <p className="muted">Loading…</p>
        </div>
      </section>
    );
  }

  if (err && !quote) {
    return (
      <section className="section stone">
        <div className="container">
          <h1>Quote</h1>
          <p className="muted">{err}</p>
          <Link className="btn" to="/">
            Back to Home
          </Link>
        </div>
      </section>
    );
  }

  if (quote?.disabled) {
    return (
      <section className="section stone">
        <div className="container">
          <h1>Quote Unavailable</h1>
          <p className="muted">This quote link has been disabled.</p>
          <Link className="btn" to="/">
            Back to Home
          </Link>
        </div>
      </section>
    );
  }

  const publicUrl = `${window.location.origin}/quote/${quote.id}`;

  return (
    <section className="section stone">
      <div className="container">
        {/* Header */}
        <div
          className="no-print"
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start",
            marginBottom: 12,
          }}
        >
          <div>
            <h1 style={{ marginBottom: 6 }}>{quote.title || "Quote"}</h1>
            <p className="muted" style={{ margin: 0 }}>
              {quote.quoteNumber ? `${quote.quoteNumber} • ` : ""}
              {createdLabel ? `Created ${createdLabel}` : ""}
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" className="btn" onClick={() => window.print()}>
              Print / Save as PDF
            </button>
          </div>
        </div>

        {/* Print-only heading */}
        <div className="print-only" style={{ display: "none", marginBottom: 14 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{quote.title || "Quote"}</div>
          <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
            {quote.quoteNumber ? `${quote.quoteNumber} • ` : ""}
            {createdLabel ? `Created ${createdLabel}` : ""}
          </div>
        </div>

        {/* Main Card */}
        <div
          className="print-card"
          style={{
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 14,
            padding: 16,
            background: "rgba(255,255,255,0.75)",
          }}
        >
          {/* Client + Address */}
          <div
            className="avoid-break"
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <div style={{ minWidth: 260 }}>
              <div className="micro muted" style={{ marginBottom: 6 }}>
                Prepared for
              </div>

              <strong style={{ display: "block", fontSize: 16 }}>
                {quote.clientName || "Client"}
              </strong>

              {quote.clientOrganization && (
                <div className="micro muted" style={{ marginTop: 2 }}>
                  {quote.clientOrganization}
                </div>
              )}

              {quote.clientEmail && <div className="micro muted">{quote.clientEmail}</div>}
              {quote.clientPhone && <div className="micro muted">{quote.clientPhone}</div>}
            </div>

            <div style={{ minWidth: 260, textAlign: "right" }}>
              <div className="micro muted" style={{ marginBottom: 6 }}>
                Address
              </div>

              {!addressBlock ? (
                <div className="micro muted">—</div>
              ) : (
                <div className="micro muted" style={{ whiteSpace: "pre-wrap" }}>
                  {addressBlock}
                </div>
              )}

              {quote.clientAddress?.region && quote.clientAddress?.country && (
                <div className="micro muted" style={{ marginTop: 6 }}>
                  ({regionName}: {quote.clientAddress.region})
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div style={{ marginTop: 16 }}>
            <div className="micro muted" style={{ marginBottom: 8 }}>
              Items
            </div>

            <div
              style={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 12,
                overflow: "hidden",
                background: "rgba(255,255,255,0.65)",
              }}
            >
              <div
                className="avoid-break"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr 0.6fr 0.8fr 0.8fr",
                  gap: 10,
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  fontWeight: 700,
                }}
              >
                <div>Item</div>
                <div style={{ textAlign: "right" }}>Qty</div>
                <div style={{ textAlign: "right" }}>Unit</div>
                <div style={{ textAlign: "right" }}>Total</div>
              </div>

              {items.length === 0 ? (
                <div style={{ padding: "12px" }} className="muted">
                  No items listed.
                </div>
              ) : (
                items.map((it, idx) => {
                  const qty = Number(it.qty || 0);
                  const unit = Number(it.unitPrice || 0);
                  const lineTotal = qty * unit;

                  return (
                    <div
                      key={idx}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.6fr 0.6fr 0.8fr 0.8fr",
                        gap: 10,
                        padding: "10px 12px",
                        borderBottom:
                          idx === items.length - 1 ? "none" : "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{it.name || "Item"}</div>
                        {it.desc && (
                          <div className="micro muted item-desc" style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                            {it.desc}
                          </div>
                        )}
                      </div>

                      <div style={{ textAlign: "right" }}>{qty}</div>
                      <div style={{ textAlign: "right" }}>{money(unit)}</div>
                      <div style={{ textAlign: "right", fontWeight: 700 }}>{money(lineTotal)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Totals */}
          <div className="avoid-break" style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
            <div style={{ width: "min(420px, 100%)" }}>
              <div
                style={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 12,
                  padding: 12,
                  background: "rgba(255,255,255,0.65)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span className="muted">Items Subtotal</span>
                  <span>{money(totals.itemsSubtotal)}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 6 }}>
                  <span className="muted">Discount</span>
                  <span>- {money(totals.discountAmount)}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 6 }}>
                  <span className="muted">Shipping</span>
                  <span>{money(totals.shippingAmount)}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 6 }}>
                  <span className="muted">Pre-Tax Subtotal</span>
                  <span>{money(totals.preTaxSubtotal)}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 6 }}>
                  <span className="muted">Tax</span>
                  <span>{money(totals.taxAmount)}</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    marginTop: 10,
                    fontWeight: 900,
                    fontSize: 18,
                  }}
                >
                  <span>Total</span>
                  <span>{money(totals.grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Public Notes */}
          {quote.publicMessage && (
            <div className="avoid-break" style={{ marginTop: 16 }}>
              <div className="micro muted" style={{ marginBottom: 8 }}>
                Notes
              </div>
              <div
                style={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 12,
                  padding: 12,
                  background: "rgba(255,255,255,0.65)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {quote.publicMessage}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ marginTop: 16 }} className="micro muted">
            If you have questions, reply to your email or contact us through the website.
          </div>

          {/* Print-only footer */}
          <div className="print-only" style={{ display: "none", marginTop: 18, fontSize: 11, opacity: 0.75 }}>
            Evon Polishing — {quote.quoteNumber || "Quote"} • {createdLabel || ""} • {publicUrl}
          </div>
        </div>
      </div>
    </section>
  );
}
